import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, MousePointerClick, TrendingUp, Megaphone, LogOut, Plus } from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const BrandDashboard = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [brand, setBrand] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/brand-portal/my-brand').then((r) => setBrand(r.data)).catch(() => null),
      api.get('/brand-portal/my-campaigns').then((r) => setCampaigns(r.data)).catch(() => []),
      api.get('/brand-portal/stats').then((r) => setStats(r.data)).catch(() => null),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="brand-dashboard">
      <div className="bg-gradient-to-r from-accent to-primary text-white sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-['Outfit'] text-xl font-bold flex items-center gap-2">
              <Megaphone className="w-6 h-6" />
              Brand Portal · Beach Pay
            </h1>
            <p className="text-xs text-white/80">{brand?.name || user?.full_name}</p>
          </div>
          <button onClick={() => { logout(); navigate('/login'); }} className="p-2 hover:bg-white/10 rounded-full">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-6">
        {!brand && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm">
            ⚠️ Sua marca ainda não foi configurada. Entre em contato com o admin para vincular sua conta a uma marca.
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl p-4 border border-border">
            <Eye className="w-6 h-6 text-primary mb-2" />
            <p className="text-xs text-muted-foreground">Impressões</p>
            <p className="font-bold text-2xl">{stats?.total_impressions || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-border">
            <MousePointerClick className="w-6 h-6 text-secondary mb-2" />
            <p className="text-xs text-muted-foreground">Cliques</p>
            <p className="font-bold text-2xl">{stats?.total_clicks || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-border">
            <TrendingUp className="w-6 h-6 text-accent mb-2" />
            <p className="text-xs text-muted-foreground">CTR</p>
            <p className="font-bold text-2xl">{stats?.ctr?.toFixed(1) || 0}%</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-border">
            <Megaphone className="w-6 h-6 text-primary mb-2" />
            <p className="text-xs text-muted-foreground">Campanhas ativas</p>
            <p className="font-bold text-2xl">{stats?.active_campaigns || 0}</p>
          </div>
        </div>

        {/* Campaigns */}
        <div className="bg-white rounded-xl border border-border">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-['Outfit'] font-bold text-lg">Suas Campanhas</h2>
            <button className="px-3 py-1.5 bg-primary text-primary-foreground rounded-full text-sm font-semibold flex items-center gap-1">
              <Plus className="w-4 h-4" />
              Nova
            </button>
          </div>
          <div className="divide-y divide-border">
            {campaigns.length === 0 ? (
              <p className="p-6 text-center text-muted-foreground">Nenhuma campanha ainda. Entre em contato com o admin para criar.</p>
            ) : (
              campaigns.map((c) => (
                <div key={c.id} className="p-4 flex items-center gap-3">
                  {c.image_url && (
                    <img src={c.image_url} alt={c.name} className="w-16 h-16 object-cover rounded-lg" />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold">{c.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {c.media_type} · {c.impressions} impressões · {c.clicks} cliques
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    c.is_active ? 'bg-secondary/20 text-secondary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {c.is_active ? 'Ativa' : 'Pausada'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandDashboard;
